<template>
  <div style="margin-bottom: 100px;">
    <h1 v-bg-typography="'h1'" :style="{ marginTop: '10px' }">
      Predictive heatmaps
      <span style="font-size: 12pt; color: #FF00A9">beta</span>
    </h1>
    <p>
      Heatmaps created: <b>{{ filtered.length }}</b> - Credits left:
      <b>{{ credits }}</b>
    </p>
    <bg-loader :dark="true" v-if="this.isLoading"></bg-loader>

    <div class="top" v-if="!this.isLoading">
      <bg-field
        class="search-input"
        label="Search"
        v-model="searchString"
        name="search"
      >
        <bg-input
          name="search"
          placeholder="Search for heatmap"
          v-model="searchString"
          style="width: 300px;"
        />
      </bg-field>
    </div>

    <div class="cards">
      <HeatmapCard
        v-for="version in filtered"
        :key="version.id"
        :heatmap="version"
        @analyze="$router.push(`/predictions/heatmaps/${version.id}`)"
        @delete="deleteHeatmap(version.id)"
      />
    </div>
  </div>
</template>

<script>
import BgTypography from "@/directives/bg-typography";
import BgButton from "@/components/bg-button";
import BgField from "@/components/bg-field";
import BgInput from "@/components/bg-input";
import BgSelect from "@/components/bg-select";
import Lottie from "vue-lottie";
import * as animationData from "./trashV2.json";
import HeatmapCard from "@/components/heatmap-card";
import BgIcon from "@/components/bg-icon";
import API from "@/api";
import BgLoader from "@/components/bg-loader";
import Urlbox from "urlbox";
import BgCheckbox from "@/components/bg-checkbox";

export default {
  components: {
    BgButton,
    BgField,
    BgInput,
    BgSelect,
    Lottie,
    HeatmapCard,
    BgIcon,
    BgLoader,
    BgCheckbox,
  },
  directives: {
    BgTypography,
  },
  data() {
    return {
      aboveFold: true,
      application: null,
      credits: "Unlimited (beta)",
      device: "",
      devices: ["Desktop", "Mobile"],
      error: false,
      errorMessage: "",
      fullPage: false,
      isLoading: false,
      selected: null,
      modal: false,
      typing: false,
      title: "Give it a title",
      options: ["UX Design", "Social", "Packaging", "Other"],
      heatmaps: [],
      searchString: "",
      defaultOptions: {
        animationData: animationData.default,
        loop: false,
        autoplay: false,
      },
      animationSpeed: 1,
      uploads: [],
      uploading: false,
      url: "",
    };
  },
  computed: {
    filtered: function() {
      function compare(a, b) {
        if (a.id > b.id) {
          return -1;
        }
        if (a.id < b.id) {
          return 1;
        }
        return 0;
      }
      let filtered = this.heatmaps.filter((el) => el.image && el.title);
      filtered.sort(compare);

      if (this.searchString && this.searchString != "") {
        filtered = filtered.filter((el) =>
          el.title.toLowerCase().includes(this.searchString)
        );
      }

      return filtered;
    },
    buttonText: function() {
      if (this.uploads.length > 1) {
        return "Generate heatmaps";
      } else {
        return "Generate heatmap";
      }
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    async addUrl(val) {
      this.uploading = true;
      // 1 fetch screenshot, add it to heatmap
      const urlbox = Urlbox(
        "8wII7dOU3EGNmOFr",
        "ebcd185c3d2f49b198a257624c141416"
      );

      let width = 1920;
      if (this.device === "Mobile") {
        width = 390;
      }

      const options = {
        format: "png",
        click_accept: true,
        hide_cookie_banners: true,
        cookie: ["cookie=OptIn=true"],
        url: this.url,
        full_page: val.full,
        width: width,
      };

      const screenshot = await urlbox.buildUrl(options);

      function toDataURL(url) {
        return new Promise((resolve, reject) => {
          var xhr = new XMLHttpRequest();
          xhr.onload = function() {
            var reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(xhr.response);
          };
          xhr.open("GET", url);
          xhr.responseType = "blob";
          xhr.send();
        });
      }
      const fileBase64 = await toDataURL(screenshot);
      let title = "Above fold";

      if (val.full === true) {
        title = "Full page";
      } else if (!val) {
        title = "";
      }

      const pretitle = String(this.device);

      this.uploads.push({
        image: fileBase64,
        title: pretitle.concat(" ", title),
      });

      if (this.fullPage && this.aboveFold && this.uploads.length === 2) {
        this.uploading = false;
        this.fullPage = false;
        this.url = "";
        this.device = "";
      } else if (
        (this.fullPage && !this.aboveFold) ||
        (!this.fullPage && this.aboveFold)
      ) {
        this.uploading = false;
        this.fullPage = false;
        this.url = "";
        this.device = "";
      }
    },
    changeApplication(val) {
      this.application = val;
    },
    async fetchData() {
      this.isLoading = true;
      this.heatmaps = [];
      const result = await API.test.getPredictions();
      result.forEach((el) => {
        this.heatmaps.push(el);
      });
      this.isLoading = false;
    },
    getScreenshots() {
      if ((!this.aboveFold && !this.fullPage) || !this.device) {
        this.error = true;
      }
      if (this.fullPage) {
        this.addUrl({ full: true });
      }
      if (this.aboveFold) {
        this.addUrl({ full: false });
      }
    },
    openInNewTab(url) {
      window.open(url, "_blank").focus();
    },
    showInput() {
      // Show the input component
      this.typing = true;

      // Focus the component, but we have to wait
      // so that it will be showing first.
      this.$nextTick(() => {
        this.focusInput();
      });
    },
    focusInput() {
      this.$refs.inputName.focus();
    },
    handleAnimation: function(anim) {
      this.anim = anim;
    },
    openDocs() {
      window.open(
        "https://braingineers.crunch.help/get-started/get-started-with-predictions",
        "_blank"
      );
    },
    start() {
      this.anim.play();
    },
    stop() {
      this.anim.stop();
    },
    trigger() {
      this.$refs.headerRef.click();
    },
    async deleteHeatmap(val) {
      let r = confirm("Are you sure?!");
      if (r == true) {
        const res = await API.test.deletePrediction(val);
        const index = this.heatmaps.findIndex((el) => el.id === val);
        this.heatmaps.splice(index, 1);
      }
    },
    async generateHeatmaps() {
      let missing = 0;
      this.uploads.forEach((el) => {
        if (!el.title) {
          missing = missing + 1;
        }
      });
      if (missing === 0) {
        this.isLoading = true;

        this.uploads.forEach(async (u) => {
          const result = await API.test.createPrediction(u);
          result.title = u.title;
          result.application = u.application;
          await API.test.generatePrediction(result.id);
          const res = await API.test.updatePrediction(result);
          this.heatmaps.push(res);
        });

        this.isLoading = false;
        this.modal = false;
        this.uploads = [];
      } else {
        this.errorMessage = true;
      }
    },
    async onFileChange() {
      const encodedImage = await this.toBase64(this.$refs.headerRef.files[0]);
      this.uploads.push({
        image: encodedImage,
        title: "",
      });
    },
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./styles.scss";

h1 {
  margin-bottom: 10px;
}

.search-input {
  max-width: 300px;
  margin-bottom: 0;
}

.cards {
  display: flex;
  gap: 25px;
  flex-wrap: wrap;
}

.top {
  display: flex;
  gap: 25px;
  margin-top: 30px;
  align-items: flex-end;
  margin-bottom: 20px;
}
.message {
  max-width: 1275px;
  background-color: #636383;
  padding: 30px 30px;
  display: flex;
  gap: 30px;
  border-radius: 6px;
  color: white;
  margin-top: 25px;
  margin-right: 80px;
  margin-bottom: 25px;
  align-items: center;
  justify-content: space-between;

  img {
    width: 50px;
    border-radius: 100%;
    border: 1px solid white;
  }
}

.message svg {
  min-width: 50px;
}
</style>
